import type { Media } from '@prisma/client';
import { MediaPlacement, MediaType } from '@prisma/client';

// Media Processing Status
export type MediaProcessingStatus = 'pending' | 'uploading' | 'processing' | 'complete' | 'error';

// Media Status Enum
export const MediaStatus = {
  PENDING: 'pending',
  PROCESSING: 'processing',
  READY: 'ready',
  ERROR: 'error',
  DELETED: 'deleted'
} as const;

export type MediaStatus = typeof MediaStatus[keyof typeof MediaStatus];

// Media Dimensions Interface
export interface MediaDimensions {
  width: number | null;
  height: number | null;
  aspectRatio: number | null;
  dpi?: number | null;
}

// Media Error Codes
export type MediaErrorCode =
  | 'VALIDATION_ERROR'
  | 'UPLOAD_ERROR'
  | 'PROCESSING_ERROR'
  | 'NOT_FOUND'
  | 'UNAUTHORIZED'
  | 'INVALID_STATE'
  | 'UNKNOWN_ERROR'
  | 'DELETE_ERROR';

// Media Error Class
export class MediaError extends Error {
  constructor(
    public code: MediaErrorCode,
    message: string,
    public details?: Record<string, unknown>
  ) {
    super(message);
    this.name = 'MediaError';
  }
}

// Base Media Interface - All media types extend from this
export interface IMediaBase {
  type: MediaType;
  placement: MediaPlacement;
  mimeType: string;
  size: number;
  metadata: IMediaMetadata;
}

// Base Media Metadata Interface
export interface IMediaMetadata {
  dimensions?: MediaDimensions;
  ownerId: string;
  alt: string | null;
  caption: string | null;
  orderIndex: number | null;
  thumbnailUrl?: string;
  duration?: number;
  description?: string;
  customPath?: string;
  mimeType?: string;
  size?: number;
  placement: MediaPlacement;
  originalName?: string | null;
  // Fields for offline fallback functionality
  needsServerUpload?: boolean;
  originalFile?: string;
  fileSize?: number;
  // Additional metadata fields
  [key: string]: unknown;
}

// Upload Progress Interface
export interface IUploadProgress {
  progress: number;
  status: MediaProcessingStatus;
  percentage: number;
  error?: MediaError | null;
  bytesUploaded?: number;
  bytesTotal?: number;
}

export interface UploadStatus {
  status: 'uploading' | 'processing' | 'complete' | 'error';
  progress: number;
  error?: string | null;
}

// Preview State Interface - Extends base with preview-specific fields
export interface IMediaPreview extends IMediaBase {
  previewId: string; // Temporary ID for preview state
  previewUrl: string;
  uploadProgress: number;
  processingStatus: MediaProcessingStatus;
  error?: MediaError | null;
  file: File;
}

/**
 * Prisma fields that are optional in the app-layer IMediaItem.
 * FK / relation keys and fields that the old interface never exposed are
 * re-declared as optional so existing construction sites don't break.
 */
type MediaOptionalKeys =
  | 'articleId'
  | 'projectId'
  | 'productId'
  | 'serviceId'
  | 'vehicleId'
  | 'appointmentId'
  | 'userProfileId'
  | 'serviceCategoryId'
  | 'shopLocationDetailsId'
  | 'shopLocationId'
  | 'productVariantId'
  | 'walkAroundCheckId'
  | 'ownerId'
  | 'orderIndex'
  | 'originalName';

/**
 * Final Media Item Interface - The persisted media state.
 *
 * Extends Prisma's `Media` model directly so that `createdAt` / `updatedAt`
 * are `Date` (matching what Prisma actually returns).  `metadata` and
 * `status` are narrowed to app-specific types.  Prisma FK fields and fields
 * that were absent from the legacy interface are kept optional so that
 * existing construction sites continue to compile.
 */
export interface IMediaItem
  extends Omit<Media, 'metadata' | 'status' | MediaOptionalKeys> {
  metadata: IMediaMetadata;
  status: MediaStatus;
  // Fields optional in app layer (present when loaded from Prisma)
  ownerId?: string;
  orderIndex?: number | null;
  originalName?: string | null;
  articleId?: string | null;
  projectId?: string | null;
  productId?: string | null;
  serviceId?: string | null;
  vehicleId?: string | null;
  appointmentId?: string | null;
  userProfileId?: string | null;
  serviceCategoryId?: string | null;
  shopLocationDetailsId?: string | null;
  shopLocationId?: string | null;
  productVariantId?: string | null;
  walkAroundCheckId?: string | null;
}

// Upload Options Interface - Simplified and type-safe
export interface IMediaUploadOptions {
  metadata: IMediaMetadata;
  onProgress?: (progress: IUploadProgress) => void;
  signal?: AbortSignal;
}

// Upload Result Interface - Consistent with service response
export interface IMediaUploadResult {
  success: boolean;
  media?: IMediaItem;
  error?: MediaError | null;
}

// Media Service Interface - Core service contract
export interface IMediaService {
  createPreview(file: File, placement: MediaPlacement, options: { ownerId: string }): Promise<IMediaPreview>;
  validateMedia(file: File, placement: MediaPlacement): Promise<MediaValidationResult>;
  upload(file: File, options: IMediaUploadOptions): Promise<IMediaUploadResult>;
  update(operation: IMediaUpdateOperation): Promise<IMediaItem>;
  delete(id: string, userId: string): Promise<void>;
  getById(id: string, userId: string): Promise<IMediaItem>;
  list(options?: {
    placement?: MediaPlacement;
    type?: MediaType;
    ownerId?: string;
    limit?: number;
    offset?: number;
  }): Promise<IMediaItem[]>;
  cleanupPreview(previewUrl: string): void;
  cleanupAllPreviews(): void;

  // Sanitization methods
  sanitizeMediaItem(mediaItem: unknown, fallbackTitle?: string): IMediaItem | null;
  sanitizeMediaArray(mediaItems: unknown[] | null | undefined, fallbackTitle?: string): IMediaItem[];
  sanitizeContentMedia(contentObject: Record<string, unknown>, fallbackTitle?: string): Record<string, unknown>;
}

// Media Update Operation Interface - Type-safe updates
export interface IMediaUpdateOperation {
  id: string;
  data: Partial<Omit<IMediaItem, 'id' | 'createdAt' | 'updatedAt' | 'metadata'>> & {
    metadata?: Partial<IMediaMetadata>;
  };
}

// Media Validation Result Interface
export interface MediaValidationResult {
  isValid: boolean;
  error?: MediaError | null;
  metadata?: Partial<IMediaMetadata>;
}

// Type Guards
export function isMediaPreview(item: unknown): item is IMediaPreview {
  return (
    typeof item === 'object' &&
    item !== null &&
    'previewId' in item &&
    'previewUrl' in item &&
    'uploadProgress' in item &&
    'processingStatus' in item
  );
}

export function isMediaItem(item: unknown): item is IMediaItem {
  return (
    typeof item === 'object' &&
    item !== null &&
    'id' in item &&
    'url' in item &&
    'status' in item &&
    'version' in item
  );
}

// Type Exports
export type MediaMetadata = IMediaMetadata;
export type UploadProgress = IUploadProgress;
export type MediaUploadOptions = IMediaUploadOptions;
export type MediaUploadResult = IMediaUploadResult;
export type MediaUpdateOperation = IMediaUpdateOperation;
export type MediaItem = IMediaItem;
export type PreviewMediaItem = IMediaPreview;

/**
 * Utility function to check if a URL is a blob URL
 * This provides a consistent way to detect blob URLs across the application
 */
export function isBlobUrl(url: string): boolean {
  return url.startsWith('blob:');
}

// Remove duplicate MediaItem type definition and schema
// The schema is now defined in media-schema.ts

